import apiClient from "./client";
import { jsonBlob } from "./adapters";
import type {
  Product,
  ProductWithDetails,
  ProductWithCategory,
  CreateProductRequest,
  UpdateProductRequest,
  ProductExtra,
} from "@/types";

interface BackendProductExtra {
  id: number;
  name: string;
  price: number;
  currency: string;
  priceOverride?: number | null;
  effectivePrice: number;
}

interface BackendProduct {
  id: number;
  name: string;
  description: string;
  price: number;
  discount?: number | null;
  currency: string;
  image?: string | null;
  visible: boolean;
}

interface BackendProductWithCategory extends BackendProduct {
  category: {
    id: number;
    name: string;
  };
}

interface BackendProductWithDetails extends BackendProductWithCategory {
  extras: BackendProductExtra[];
}

function mapProduct(product: BackendProduct): Product {
  return {
    id: product.id,
    name: product.name,
    description: product.description,
    price: product.price,
    discount: product.discount ?? 0,
    currency: product.currency,
    image: product.image ?? "",
    visible: product.visible,
  };
}

function mapProductWithCategory(
  product: BackendProductWithCategory
): ProductWithCategory {
  return {
    ...mapProduct(product),
    category: product.category,
  };
}

function mapProductExtra(extra: BackendProductExtra): ProductExtra {
  return {
    id: extra.id,
    restaurantExtraId: extra.id,
    name: extra.name,
    price: extra.price,
    currency: extra.currency,
    priceOverride: extra.priceOverride ?? undefined,
    effectivePrice: extra.effectivePrice,
  };
}

function mapProductWithDetails(
  product: BackendProductWithDetails
): ProductWithDetails {
  return {
    ...mapProductWithCategory(product),
    extras: product.extras.map(mapProductExtra),
  };
}

function appendProductPayload(
  formData: FormData,
  data: CreateProductRequest | UpdateProductRequest
) {
  formData.append(
    "product",
    jsonBlob({
      name: data.name,
      description: data.description,
      price: data.price,
      discount: data.discount,
      currency: data.currency,
      visible: data.visible,
      categoryId: data.categoryId,
      extras: data.extras,
    })
  );
}

export const productsApi = {
  getProducts: async (): Promise<Product[]> => {
    const response = await apiClient.get<{ products: BackendProductWithCategory[] }>(
      "/product/for-restaurant"
    );
    return response.data.products.map(mapProduct);
  },

  getProduct: async (id: number): Promise<ProductWithDetails> => {
    const response = await apiClient.get<BackendProductWithDetails>(
      `/product/${id}`
    );
    return mapProductWithDetails(response.data);
  },

  createProduct: async (data: CreateProductRequest): Promise<Product> => {
    const formData = new FormData();
    appendProductPayload(formData, data);

    if (data.image) {
      formData.append("image", data.image);
    }

    const response = await apiClient.post<BackendProductWithDetails>(
      "/product",
      formData
    );
    return mapProduct(response.data);
  },

  updateProduct: async (
    id: number,
    data: UpdateProductRequest
  ): Promise<Product> => {
    const formData = new FormData();
    appendProductPayload(formData, data);
    if (data.image) formData.append("image", data.image);

    const response = await apiClient.patch<BackendProductWithDetails>(
      `/product/${id}`,
      formData
    );
    return mapProduct(response.data);
  },

  deleteProduct: async (id: number): Promise<void> => {
    await apiClient.delete(`/product/${id}`);
  },
};
